/* eslint-disable react/require-default-props */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Button, FlexColumn, FlexRow, Typography } from 'design-system';
import { CopyLinkIcon, ExpandAltIcon, EyeIcon, FileIcon, OpenLinkIcon, SpinnerIcon } from 'design-system/components/Icons';
import useGetFileSize from '../hooks/use-get-file-size';
import {
  isLocalUploadFile,
  resolveClientConnectIntroductionVideoSrc,
  resolveClientConnectMaterialFileUrl,
} from '../helpers/invitation-preview-helpers';
import PreviewMaterialModal from './PreviewMaterialModal';

function formatVideoDuration(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return null;
  }
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

function PlayTriangleIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="currentColor" aria-hidden>
      <path d="M10 6.5L22 14L10 21.5V6.5Z" />
    </svg>
  );
}

function formatFileMeta(content, getFileSize) {
  if (content?.link) {
    return content.link;
  }
  const ext = (content?.extension || '').replace('.', '').toUpperCase() || 'FILE';
  const size = content?.file ? getFileSize(content.file) : '';
  if (size) {
    return `${ext} · ${size}`;
  }
  return ext;
}

function resolvePreviewKind(content) {
  if (content?.link) {
    return 'link';
  }
  const ext = (content?.extension || content?.file?.type || '').toLowerCase();
  if (ext.includes('pdf') || ext === 'application/pdf') {
    return 'pdf';
  }
  if (ext.includes('image') || ['jpeg', 'jpg', 'png', 'svg', 'gif', 'webp'].some((t) => ext.includes(t))) {
    return 'image';
  }
  return 'document';
}

function resolveVideoPreviewUrlSync(introductionVideo) {
  if (!introductionVideo || isLocalUploadFile(introductionVideo)) {
    return null;
  }
  if (typeof introductionVideo === 'string' && introductionVideo.trim()) {
    const trimmed = introductionVideo.trim();
    if (
      trimmed.startsWith('blob:') ||
      trimmed.startsWith('data:') ||
      trimmed.startsWith('http://') ||
      trimmed.startsWith('https://') ||
      trimmed.startsWith('/')
    ) {
      return trimmed;
    }
  }
  const serverUrl = resolveClientConnectIntroductionVideoSrc(introductionVideo);
  if (serverUrl) {
    return serverUrl;
  }
  if (typeof introductionVideo?.video === 'string' && introductionVideo.video.trim()) {
    return introductionVideo.video.trim();
  }
  return null;
}

export default function GettingStartedPreview({
  introductionVideo,
  introductionVideoTitle = '',
  clientContent,
  onOpenVideo,
  accentColor,
}) {
  const intl = useIntl();
  const getFileSize = useGetFileSize();
  const [previewMaterial, setPreviewMaterial] = useState(null);
  const [blobUrls, setBlobUrls] = useState({});
  const [videoDurationLabel, setVideoDurationLabel] = useState(null);
  const [blobPreviewUrl, setBlobPreviewUrl] = useState(null);
  const [isVideoPreviewReady, setIsVideoPreviewReady] = useState(false);
  const [videoPreviewError, setVideoPreviewError] = useState(false);

  const syncPreviewUrl = useMemo(
    () => resolveVideoPreviewUrlSync(introductionVideo),
    [introductionVideo],
  );
  const videoPreviewUrl = blobPreviewUrl || syncPreviewUrl;

  const handlePreviewLoadedMetadata = (event) => {
    const el = event.currentTarget;
    setVideoDurationLabel(formatVideoDuration(el.duration));
    setIsVideoPreviewReady(true);
    setVideoPreviewError(false);
    try {
      if (el.currentTime < 0.01) {
        el.currentTime = 0.01;
      }
    } catch {
      // Some browsers reject seeks until enough data is buffered.
    }
  };

  const handlePreviewError = () => {
    setIsVideoPreviewReady(false);
    setVideoPreviewError(true);
  };

  const materials = useMemo(() => (clientContent || []).filter((item) => item?.file || item?.link), [clientContent]);

  useEffect(() => {
    const next = {};
    const created = [];
    materials.forEach((item, index) => {
      if (item.file instanceof File) {
        const url = URL.createObjectURL(item.file);
        next[`material-${index}`] = url;
        created.push(url);
        return;
      }
      const resolved = resolveClientConnectMaterialFileUrl(item);
      if (resolved) {
        next[`material-${index}`] = resolved;
      }
    });
    setBlobUrls(next);
    return () => {
      created.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [materials]);

  useEffect(() => {
    if (!introductionVideo || !isLocalUploadFile(introductionVideo)) {
      setBlobPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(introductionVideo);
    setBlobPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [introductionVideo]);

  useEffect(() => {
    setIsVideoPreviewReady(false);
    setVideoPreviewError(false);
    setVideoDurationLabel(null);
  }, [videoPreviewUrl]);

  useEffect(() => {
    if (!videoPreviewUrl || isVideoPreviewReady || videoPreviewError) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setIsVideoPreviewReady(true);
    }, 5000);

    return () => window.clearTimeout(timeoutId);
  }, [videoPreviewUrl, isVideoPreviewReady, videoPreviewError]);

  const openMaterial = (content, index) => {
    if (content?.link) {
      window.open(content.link, '_blank', 'noopener,noreferrer');
      return;
    }
    const fileUrl = blobUrls[`material-${index}`] || resolveClientConnectMaterialFileUrl(content);
    if (!fileUrl) {
      return;
    }
    setPreviewMaterial({
      kind: resolvePreviewKind(content),
      name: content.name,
      fileUrl,
      downloadName: content.name,
      extension: content.extension,
    });
  };

  const hasVideo = Boolean(introductionVideo);
  const hasMaterials = materials.length > 0;
  const videoCaption = useMemo(() => {
    const trimmed = introductionVideoTitle?.trim();
    if (trimmed) {
      return trimmed;
    }
    return intl.formatMessage({ id: 'client-connect-preview-welcome' });
  }, [introductionVideoTitle, intl]);
  const canResolveVideoUrl = Boolean(syncPreviewUrl) || isLocalUploadFile(introductionVideo);
  const showVideoLoading =
    hasVideo && !videoPreviewError && canResolveVideoUrl && (!videoPreviewUrl || !isVideoPreviewReady);
  const showVideoFallback = hasVideo && (videoPreviewError || !canResolveVideoUrl);

  const gettingStartedClassName = [
    'cc-preview-getting-started',
    hasVideo && hasMaterials ? 'cc-preview-getting-started--with-video' : '',
    hasVideo && !hasMaterials ? 'cc-preview-getting-started--video-only' : '',
    !hasVideo && hasMaterials ? 'cc-preview-getting-started--resources-only' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const resourcesClassName = [
    'cc-preview-resources',
    hasVideo ? 'cc-preview-resources--stack' : 'cc-preview-resources--grid',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <FlexRow align="stretch" fullWidth className={gettingStartedClassName}>
        {hasVideo ? (
          <FlexColumn
            align="center"
            justify="center"
            fullWidth={false}
            className={[
              'cc-preview-video-panel',
              isVideoPreviewReady ? 'cc-preview-video-panel--preview-ready' : '',
              showVideoLoading ? 'cc-preview-video-panel--awaiting-preview' : '',
              showVideoFallback ? 'cc-preview-video-panel--fallback' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            role="button"
            tabIndex={0}
            onClick={onOpenVideo}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onOpenVideo();
              }
            }}
          >
            {videoPreviewUrl ? (
              <video
                key={videoPreviewUrl}
                className="cc-preview-video-panel__video"
                src={videoPreviewUrl}
                muted
                playsInline
                preload="metadata"
                onLoadedMetadata={handlePreviewLoadedMetadata}
                onError={handlePreviewError}
                aria-hidden
              />
            ) : null}
            {showVideoLoading ? (
              <FlexRow
                align="center"
                justify="center"
                fullWidth
                className="cc-preview-video-panel__loading"
                role="status"
                aria-live="polite"
                aria-busy="true"
              >
                <SpinnerIcon
                  className="ds-icon--spin"
                  size={32}
                  trackFill="var(--pal-grey-blue-200)"
                  arcFill="var(--ds-theme-action-primary, var(--pal-grey-slate-600))"
                  arc2Fill="var(--ds-theme-action-primary, var(--pal-grey-slate-600))"
                  aria-label={intl.formatMessage({ id: 'loading' })}
                />
              </FlexRow>
            ) : null}
            <FlexRow
              inline
              align="center"
              fullWidth={false}
              className="cc-preview-video-panel__popout"
              role="button"
              tabIndex={0}
              onClick={(event) => {
                event.stopPropagation();
                onOpenVideo();
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  event.stopPropagation();
                  onOpenVideo();
                }
              }}
            >
              <ExpandAltIcon size={11} aria-hidden />
              {intl.formatMessage({ id: 'client-connect-preview-pop-out' })}
            </FlexRow>
            <FlexRow align="center" justify="center" className="cc-preview-video-panel__play">
              <PlayTriangleIcon />
            </FlexRow>
            <Typography component="p" className="cc-preview-video-panel__caption">
              {videoCaption}
              {videoDurationLabel ? ` · ${videoDurationLabel}` : ''}
            </Typography>
          </FlexColumn>
        ) : null}

        {hasMaterials ? (
          <FlexColumn align="stretch" fullWidth={!hasVideo} className={resourcesClassName}>
            {materials.map((content, index) => {
              const isLink = Boolean(content?.link);
              return (
                <FlexColumn
                  align="start"
                  key={content?.id || content?._id || `material-${index}`}
                  className="cc-preview-resource-card"
                >
                  <FlexRow align="center" fullWidth className="cc-preview-resource-card__row">
                    <FlexRow align="center" justify="center" className="cc-preview-resource-card__icon">
                      {isLink ? <CopyLinkIcon size={20} aria-hidden /> : <FileIcon size={20} aria-hidden />}
                    </FlexRow>
                    <FlexColumn align="start" fullWidth={false} className="cc-preview-resource-card__meta">
                      <Typography component="p" className="cc-preview-resource-card__title">
                        {content.name}
                      </Typography>
                      <Typography component="p" className="cc-preview-resource-card__subtitle">
                        {formatFileMeta(content, getFileSize)}
                      </Typography>
                    </FlexColumn>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className={[
                        'cc-preview-resource-card__action',
                        isLink ? 'cc-preview-resource-card__action--open' : 'cc-preview-resource-card__action--view',
                      ].join(' ')}
                      icon={
                        isLink ? (
                          <OpenLinkIcon size={15} stroke="currentColor" aria-hidden />
                        ) : (
                          <EyeIcon size={15} fill="currentColor" aria-hidden />
                        )
                      }
                      onClick={() => {
                        if (isLink) {
                          window.open(content.link, '_blank', 'noopener,noreferrer');
                          return;
                        }
                        openMaterial(content, index);
                      }}
                    >
                      {isLink
                        ? intl.formatMessage({ id: 'client-connect-preview-open' })
                        : intl.formatMessage({ id: 'client-connect-preview-view' })}
                    </Button>
                  </FlexRow>
                  {content?.description ? (
                    <Typography component="p" className="cc-preview-resource-card__description">
                      {content.description}
                    </Typography>
                  ) : null}
                </FlexColumn>
              );
            })}
          </FlexColumn>
        ) : null}
      </FlexRow>

      <PreviewMaterialModal
        open={Boolean(previewMaterial)}
        onClose={() => setPreviewMaterial(null)}
        material={previewMaterial}
        accentColor={accentColor}
      />
    </>
  );
}

GettingStartedPreview.propTypes = {
  introductionVideo: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.bool,
    PropTypes.instanceOf(File),
  ]),
  introductionVideoTitle: PropTypes.string,
  clientContent: PropTypes.arrayOf(PropTypes.shape({})),
  onOpenVideo: PropTypes.func.isRequired,
  accentColor: PropTypes.string,
};
